import find from 'lodash/find'
import isNumber from 'lodash/isNumber'
import noop from 'lodash/noop'
import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { Link } from '@Components/Link'
import { handleEvent } from '@Events/Manager'
import { NavigationEvent } from '@Events/Navigation'
import { OtherEvent } from '@Events/Other'
import { MetadataContext } from '@Globals/Metadata'
import { down, up } from '@Globals/Style/breakpoints'
import { flexCenterChildrenColumn, sectionInner } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

import { MegaMenu } from '../MegaMenu'
import { NavBarButton } from './NavBarButton'

const defaultState = {
  isFrozen: false,
  isHovering: false,
  isOpen: false,
}

type State = {
  activeID?: number
  isFrozen: boolean
  isHovering: boolean
  isOpen: boolean
  menuHoverID?: number
}

interface NavBarProps {
  blocked: boolean
  onBackdropChange: (showBackdrop: boolean) => void
}

interface WrapperProps {
  bottomBorderColor?: string
  color?: string
  otherLinkAfterColor?: string
  otherLinkTextColor?: string
  topBorderColor?: string
}

const Wrapper = styled.div<WrapperProps>`
  position: relative;
  width: 100%;
  max-width: 100%;
  justify-content: center;
  padding-left: 30px;
  padding-right: 30px;
  ${flexCenterChildrenColumn};
  z-index: ${({ theme }) => theme.zIndexes.desktopHeaderNavMenu};
  /* TODO: Include in theme/colors system as greyLightest darkened 5% */
  border-top: 1px solid ${({ topBorderColor }) => topBorderColor ?? '#e8e8e8'};
  border-bottom: 1px solid ${({ bottomBorderColor }) => bottomBorderColor ?? '#e8e8e8'};
  background-color: ${({ color, theme }) => color ?? theme.colors.white};
  display: none;

  ${up('desktop')} {
    display: flex;
  }

  .navBarInner {
    ${sectionInner};
    display: flex;
    align-items: stretch;
    justify-content: space-between;
  }

  .categoriesWrap {
    position: relative;
    display: flex;
  }

  .otherLinks {
    position: relative;
    display: flex;
    align-items: stretch;
  }

  .otherLinkItem {
    position: relative;
    padding: 16px 12px;
    font-size: 0.75rem;
    color: ${({ otherLinkTextColor, theme }) => otherLinkTextColor ?? theme.colors.greyDark};
    text-transform: uppercase;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      margin-left: 0;
      background-color: ${({ otherLinkAfterColor, theme }) => otherLinkAfterColor
        ?? theme.colors.greyDark};
    }

    &:hover {
      &:after {
        margin-left: -25%;
        width: 50%;
      }
    }

    ${down('maxWidth')} {
      padding: 16px 10px;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`

export const NavBar: FC<NavBarProps> = (
  {
    blocked,
    onBackdropChange,
  }: NavBarProps,
) => {
  const { t } = useTranslation()
  const { categories, storeURL } = useContext(MetadataContext)
  const outletCategory = useMemo(() => find(categories, ['id', '224']), [categories])
  const [
    {
      activeID,
      isFrozen,
      isHovering,
      isOpen,
      menuHoverID,
    },
    setState,
  ] = useState<State>(defaultState)
  const categoryButtons = [
    {
      title: t('header:ALL_ACCESSORIES_LABEL'),
    },
    {
      title: t('header:SUIT_ACCESSORIES_LABEL'),
    },
    {
      title: t('header:JEWELLERY_LABEL'),
    },
    {
      title: t('header:BAGS_AND_WALLETS_LABEL'),
    },
  ]

  const openMegaMenu = (id: number) => {
    handleEvent(
      new OtherEvent({
        attributes: {
          menu_index: id,
        },
        eventName: 'nav_desktop_mega_menu_open',
      }),
    )

    setState(prev => ({
      ...prev,
      activeID: id,
      isHovering: true,
      isOpen: true,
    }))
  }

  const closeMegaMenu = (forceClose: boolean = false) => {
    if (isFrozen && !forceClose) {
      return
    }

    setState(prev => ({
      ...prev,
      activeID: undefined,
      isFrozen: false,
      isHovering: false,
      isOpen: false,
      menuHoverID: undefined,
    }))
  }

  const onCategoryClick = (id: number) => {
    if (activeID === id && isOpen) {
      closeMegaMenu()
      return
    }

    openMegaMenu(id)
  }

  const onCategoryMouseEnter = (id: number) => {
    setState(prev => ({
      ...prev,
      isFrozen: prev.isFrozen || !prev.isOpen,
      isHovering: true,
      menuHoverID: id,
    }))
  }

  const onCategoryMouseLeave = (id: number) => {
    setState(prev => ({
      ...prev,
      isHovering: false,
      menuHoverID: prev.menuHoverID !== id ? prev.menuHoverID : undefined,
    }))
  }

  const onMenuLeave = () => {
    closeMegaMenu(true)
  }

  // Handle hover open delay
  useEffect(() => {
    if (isOpen || !isHovering) {
      return noop
    }

    const timeout = setTimeout(
      () => {
        setState(prev => ({
          ...prev,
          activeID: prev.menuHoverID,
          isOpen: true,
        }))
      },
      250,
    )

    return () => {
      clearTimeout(timeout)
    }
  }, [isHovering, isOpen])

  // Clear isFrozen after a second
  useEffect(() => {
    if (!isFrozen) {
      return noop
    }

    const timeout = setTimeout(() => {
      setState(prev => ({
        ...prev,
        isFrozen: false,
      }))
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [isFrozen])

  // Show/hide backdrop
  useEffect(() => {
    onBackdropChange(isOpen)
  }, [isOpen, onBackdropChange])

  // Handle delay between hovering different menu ids
  useEffect(() => {
    if (!isOpen || menuHoverID === null) {
      return noop
    }

    const timeout = setTimeout(() => {
      if (!isNumber(menuHoverID)) {
        return
      }
      openMegaMenu(menuHoverID)
    }, 250)

    return () => {
      clearTimeout(timeout)
    }
  }, [isOpen, menuHoverID])

  return (
    <Wrapper>
      <div className="navBarInner">
        {/* Categories */}
        <div
          className="categoriesWrap"
          onMouseLeave={onMenuLeave}
        >
          {categoryButtons.map((item, i) => (
            <NavBarButton
              key={item.title}
              onCategoryClick={() => onCategoryClick(i)}
              onCategoryMouseEnter={() => onCategoryMouseEnter(i)}
              onCategoryMouseLeave={() => onCategoryMouseLeave(i)}
              title={item.title}
            />
          ))}

          {/* Mega menu */}
          <MegaMenu activeID={activeID} isOpen={!blocked && isOpen} />
        </div>

        {/* Other links */}
        <div className="otherLinks">
          {outletCategory && (
            <Link
              className="otherLinkItem"
              event={new NavigationEvent({
                attributes: {
                  dest_page_type: 'category',
                  dest_page_type_id: outletCategory.id,
                  source_id: 'header',
                },
                eventName: 'nav_link_click',
              })}
              href={outletCategory.url || ''}
            >
              {outletCategory.name}
            </Link>
          )}
          <Link
            className="otherLinkItem"
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'cms_category_collections',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={storeURL('collections')}
          >
            {t('header:COLLECTIONS_LABEL')}
          </Link>
          <Link
            className="otherLinkItem"
            event={new NavigationEvent({
              attributes: {
                dest_page_type: 'cms_category_articles',
                dest_page_type_id: '',
                source_id: 'header',
              },
              eventName: 'nav_link_click',
            })}
            href={storeURL('articles')}
          >
            {t('header:ARTICLES_LABEL')}
          </Link>
        </div>
      </div>
    </Wrapper>
  )
}
